// @ts-strict-ignore
import { Box } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { analyticsMessages } from "../../messages";
import { CohortRow } from "../../models/analytics";
import { EmptyState } from "../EmptyState";
import { surfaceCardStyle } from "./tokens";

interface CohortTableProps {
  title: string;
  description?: string;
  rows: CohortRow[];
  formatMoney?: (v: number) => string;
  headerActions?: React.ReactNode;
  loading?: boolean;
  na?: boolean;
  emptyMessage?: string;
}

const formatCohort = (s: string) => {
  if (!s || s.length < 7) return s;

  const [y, m] = s.split("-");
  const d = new Date(Date.UTC(Number(y), Number(m) - 1, 1));

  return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
};

const colorForRepeat = (r: number) => {
  if (r >= 0.3) return "#10B981";

  if (r >= 0.15) return "#F59E0B";

  return "#9CA3AF";
};

export const CohortTable: React.FC<CohortTableProps> = ({
  title,
  description,
  rows,
  formatMoney,
  headerActions,
  loading,
  na,
  emptyMessage,
}) => {
  const intl = useIntl();
  const empty = !na && !loading && (!rows || rows.length === 0);

  return (
    <Box padding={5} style={{ ...surfaceCardStyle }}>
      <Box marginBottom={4} display="flex" justifyContent="space-between" alignItems="flex-start" gap={3}>
        <Box>
          <Box as="div" fontSize={2} color="default2">
            {title}
          </Box>
          {description && (
            <Box as="div" fontSize={1} color="default2" marginTop={1} style={{ opacity: 0.8 }}>
              {description}
            </Box>
          )}
        </Box>
        {headerActions}
      </Box>

      {na ? (
        <Box padding={6} textAlign="center" color="default2">
          {intl.formatMessage(analyticsMessages.commonNA)}
        </Box>
      ) : loading ? (
        <Box padding={6} textAlign="center" color="default2">
          {intl.formatMessage(analyticsMessages.commonLoading)}
        </Box>
      ) : empty ? (
        <EmptyState message={emptyMessage ?? intl.formatMessage(analyticsMessages.commonNoData)} />
      ) : (
        <Box as="div" overflowX="auto">
          <table
            style={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: 0,
              fontSize: 13,
            }}
          >
            <thead>
              <tr>
                {[
                  { label: "Cohort", align: "left" as const },
                  { label: "Customers", align: "right" as const },
                  { label: "Repeat rate", align: "right" as const },
                  { label: "Avg orders", align: "right" as const },
                  { label: "Avg GMV", align: "right" as const },
                ].map(h => (
                  <th
                    key={h.label}
                    style={{
                      textAlign: h.align,
                      padding: "8px 12px",
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#6B7280",
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                    }}
                  >
                    {h.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const repeatColor = colorForRepeat(r.repeatRate);
                const bottomBorder =
                  i === rows.length - 1 ? "none" : "1px solid rgba(15, 23, 42, 0.06)";

                return (
                  <tr
                    key={r.cohort}
                    style={{ transition: "background 120ms ease" }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLTableRowElement).style.background =
                        "rgba(15, 23, 42, 0.025)";
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLTableRowElement).style.background = "transparent";
                    }}
                  >
                    <td
                      style={{
                        padding: "10px 12px",
                        fontWeight: 600,
                        color: "#111827",
                        borderBottom: bottomBorder,
                      }}
                    >
                      {formatCohort(r.cohort)}
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        textAlign: "right",
                        fontFeatureSettings: '"tnum"',
                        color: "#374151",
                        borderBottom: bottomBorder,
                      }}
                    >
                      {r.size}
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        textAlign: "right",
                        fontFeatureSettings: '"tnum"',
                        color: repeatColor,
                        fontWeight: 700,
                        borderBottom: bottomBorder,
                      }}
                    >
                      {(r.repeatRate * 100).toFixed(1)}%
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        textAlign: "right",
                        fontFeatureSettings: '"tnum"',
                        color: "#374151",
                        borderBottom: bottomBorder,
                      }}
                    >
                      {r.avgOrders.toFixed(1)}
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        textAlign: "right",
                        fontFeatureSettings: '"tnum"',
                        color: "#111827",
                        fontWeight: 600,
                        borderBottom: bottomBorder,
                      }}
                    >
                      {formatMoney ? formatMoney(r.avgGmv) : r.avgGmv.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      )}
    </Box>
  );
};
